import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Collapse,
	IconButton,
	LinearProgress,
	Link,
	Paper,
	Stack,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import { useSnackbar } from 'notistack';
import {
	dismissProOnboarding,
	fetchProOnboarding,
	skipProOnboardingStep,
} from '../Shared/Api';
import { PRO_ONBOARDING_REFRESH_EVENT } from '../Shared/onboarding';
import useConfirm from './useConfirm';

const ProOnboardingChecklist = () => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const navigate = useNavigate();
	const location = useLocation();
	const { enqueueSnackbar } = useSnackbar();
	const { confirm, confirmDialog } = useConfirm();
	const [state, setState] = useState(null);
	const [loading, setLoading] = useState(true);
	const [expanded, setExpanded] = useState(false);
	const [actionLoading, setActionLoading] = useState(null);
	const hasAutoExpanded = useRef(false);

	const loadOnboarding = useCallback(async () => {
		try {
			const response = await fetchProOnboarding();
			setState(response);
		} catch (error) {
			console.error('Failed to load Pro onboarding checklist:', error);
			setState(null);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		loadOnboarding();
	}, [loadOnboarding, location.pathname, location.search]);

	useEffect(() => {
		const handleRefresh = () => {
			loadOnboarding();
		};
		window.addEventListener('focus', handleRefresh);
		window.addEventListener(PRO_ONBOARDING_REFRESH_EVENT, handleRefresh);
		return () => {
			window.removeEventListener('focus', handleRefresh);
			window.removeEventListener(PRO_ONBOARDING_REFRESH_EVENT, handleRefresh);
		};
	}, [loadOnboarding]);

	const steps = useMemo(() => state?.steps ?? [], [state?.steps]);

	const progressCounts = useMemo(() => {
		const completed = steps.filter((step) => step.complete).length;
		return { completed, total: steps.length };
	}, [steps]);

	useEffect(() => {
		if (!state?.visible || hasAutoExpanded.current) {
			return;
		}
		const onJobsHome = location.pathname === '/jobs';
		if (onJobsHome && progressCounts.completed === 0) {
			setExpanded(true);
			hasAutoExpanded.current = true;
		}
	}, [state?.visible, progressCounts.completed, location.pathname]);

	const incompleteSteps = useMemo(
		() => steps.filter((step) => !step.complete),
		[steps],
	);

	const nextStepId = incompleteSteps[0]?.id;

	const handleDismiss = async () => {
		const confirmed = await confirm(
			'Hide the Pro setup guide? You can bring it back anytime from Settings.',
			{
				title: 'Hide Pro setup guide',
				confirmText: 'Hide',
			},
		);
		if (!confirmed) {
			return;
		}

		setActionLoading('dismiss');
		try {
			const response = await dismissProOnboarding();
			setState(response);
		} catch (error) {
			enqueueSnackbar(error.message || 'Could not hide checklist.', {
				variant: 'error',
			});
		} finally {
			setActionLoading(null);
		}
	};

	const handleSkip = async (stepId) => {
		setActionLoading(`skip-${stepId}`);
		try {
			const response = await skipProOnboardingStep(stepId);
			setState(response);
		} catch (error) {
			enqueueSnackbar(error.message || 'Could not skip step.', {
				variant: 'error',
			});
		} finally {
			setActionLoading(null);
		}
	};

	const handleStepAction = (stepId) => {
		setExpanded(false);
		switch (stepId) {
			case 'team':
				navigate('/users');
				break;
			case 'shop_location':
				navigate('/settings?section=time');
				break;
			case 'job_sites':
				navigate('/settings?section=time');
				break;
			case 'sms':
				navigate('/settings?section=sms');
				break;
			case 'schedule':
				navigate('/schedule');
				break;
			case 'time':
				navigate('/time');
				break;
			case 'analytics':
				navigate('/analytics');
				break;
			default:
				break;
		}
	};

	const getStepAction = (step) => {
		switch (step.id) {
			case 'team':
				return { label: 'Manage users', action: () => handleStepAction('team') };
			case 'shop_location':
				return {
					label: 'Set shop location',
					action: () => handleStepAction('shop_location'),
				};
			case 'job_sites':
				return {
					label: 'Open Time settings',
					action: () => handleStepAction('job_sites'),
				};
			case 'sms':
				return {
					label: 'Set up SMS',
					action: () => handleStepAction('sms'),
				};
			case 'schedule':
				return {
					label: 'Open Schedule',
					action: () => handleStepAction('schedule'),
				};
			case 'time':
				return { label: 'Open Time', action: () => handleStepAction('time') };
			case 'analytics':
				return {
					label: 'Open Analytics',
					action: () => handleStepAction('analytics'),
				};
			default:
				return null;
		}
	};

	if (loading || !state?.visible) {
		return null;
	}

	const progress =
		progressCounts.total > 0
			? (progressCounts.completed / progressCounts.total) * 100
			: 0;

	const widgetSx = {
		maxWidth: isMobile ? (expanded ? 'calc(100vw - 32px)' : 'auto') : 340,
		width: isMobile ? (expanded ? 'calc(100vw - 32px)' : 'auto') : 340,
	};

	const progressRing = (
		<Box sx={{ position: 'relative', display: 'inline-flex' }}>
			<CircularProgress
				variant="determinate"
				value={progress}
				size={isMobile && !expanded ? 48 : 36}
				thickness={4}
				color="warning"
			/>
			<Box
				sx={{
					top: 0,
					left: 0,
					bottom: 0,
					right: 0,
					position: 'absolute',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				<Typography
					variant="caption"
					sx={{ fontWeight: 700, fontSize: isMobile && !expanded ? 11 : 10 }}
				>
					{progressCounts.completed}/{progressCounts.total}
				</Typography>
			</Box>
		</Box>
	);

	return (
		<>
			{confirmDialog}
			<Box sx={widgetSx}>
				<Collapse in={!expanded}>
					{isMobile ? (
						<Paper
							elevation={6}
							onClick={() => setExpanded(true)}
							aria-label="Open Pro setup guide"
							sx={{
								width: 56,
								height: 56,
								borderRadius: '50%',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								cursor: 'pointer',
								border: (t) => `1px solid ${t.palette.divider}`,
								'&:hover': { bgcolor: 'action.hover' },
							}}
						>
							{progressRing}
						</Paper>
					) : (
						<Paper
							elevation={6}
							onClick={() => setExpanded(true)}
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 1.5,
								px: 2,
								py: 1.25,
								borderRadius: 999,
								cursor: 'pointer',
								border: (t) => `1px solid ${t.palette.divider}`,
								'&:hover': { bgcolor: 'action.hover' },
							}}
						>
							{progressRing}
							<Box sx={{ minWidth: 0, flex: 1 }}>
								<Typography
									variant="subtitle2"
									sx={{ fontWeight: 700, lineHeight: 1.2 }}
								>
									Set up Pro ops
								</Typography>
								<Typography variant="caption" color="text.secondary" noWrap>
									{incompleteSteps[0]?.title ?? 'Continue setup'}
								</Typography>
							</Box>
							<BuildCircleOutlinedIcon color="warning" fontSize="small" />
						</Paper>
					)}
				</Collapse>

				<Collapse in={expanded}>
					<Paper
						elevation={8}
						sx={{
							borderRadius: 2,
							border: (t) => `1px solid ${t.palette.divider}`,
							overflow: 'hidden',
							display: 'flex',
							flexDirection: 'column',
							maxHeight: 'min(60vh, 420px)',
						}}
					>
						<Stack
							direction="row"
							alignItems="flex-start"
							justifyContent="space-between"
							spacing={1}
							sx={{
								px: 2,
								pt: 2,
								pb: 1.5,
								borderBottom: (t) => `1px solid ${t.palette.divider}`,
							}}
						>
							<Box sx={{ minWidth: 0 }}>
								<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
									Set up Pro ops
								</Typography>
								<Typography variant="caption" color="text.secondary">
									{progressCounts.completed} of {progressCounts.total} complete
								</Typography>
							</Box>
							<Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
								<IconButton
									size="small"
									onClick={() => setExpanded(false)}
									aria-label="Minimize Pro checklist"
								>
									<ExpandMoreIcon fontSize="small" />
								</IconButton>
								<IconButton
									size="small"
									onClick={handleDismiss}
									disabled={actionLoading === 'dismiss'}
									aria-label="Hide Pro setup guide"
								>
									<CloseIcon fontSize="small" />
								</IconButton>
							</Stack>
						</Stack>

						<LinearProgress
							variant="determinate"
							value={progress}
							color="warning"
							sx={{ height: 3 }}
						/>

						<Box sx={{ overflowY: 'auto', px: 2, py: 2 }}>
							<Stack spacing={1.75}>
								{incompleteSteps.map((step) => {
									const stepAction = getStepAction(step);
									const isNext = step.id === nextStepId;
									return (
										<Box
											key={step.id}
											sx={{
												borderRadius: 1.5,
												px: 1.25,
												py: 1.25,
												bgcolor: isNext ? 'action.hover' : 'transparent',
												border: (t) =>
													isNext
														? `1px solid ${t.palette.warning.main}33`
														: '1px solid transparent',
											}}
										>
											<Stack
												direction="row"
												spacing={1.25}
												alignItems="flex-start"
											>
												<RadioButtonUncheckedIcon
													color={isNext ? 'warning' : 'disabled'}
													sx={{ mt: 0.15, fontSize: 20 }}
												/>
												<Box sx={{ minWidth: 0, flex: 1 }}>
													<Stack
														direction="row"
														spacing={1}
														alignItems="center"
														flexWrap="wrap"
													>
														<Typography
															variant="body2"
															sx={{ fontWeight: 600 }}
														>
															{step.title}
														</Typography>
														{step.skippable && (
															<Chip
																label="Optional"
																size="small"
																variant="outlined"
																sx={{ height: 20, fontSize: 11 }}
															/>
														)}
													</Stack>
													<Typography
														variant="caption"
														color="text.secondary"
														sx={{ display: 'block', mt: 0.25, lineHeight: 1.4 }}
													>
														{step.description}
													</Typography>

													{stepAction && (
														<Button
															size="small"
															variant="contained"
															color="warning"
															onClick={stepAction.action}
															sx={{ mt: 1.25, textTransform: 'none' }}
														>
															{stepAction.label}
														</Button>
													)}

													{step.skippable && (
														<Link
															component="button"
															type="button"
															variant="caption"
															onClick={() => handleSkip(step.id)}
															disabled={actionLoading === `skip-${step.id}`}
															sx={{
																display: 'block',
																mt: 1,
																textAlign: 'left',
															}}
														>
															Skip for now
														</Link>
													)}
												</Box>
											</Stack>
										</Box>
									);
								})}
							</Stack>
						</Box>
					</Paper>
				</Collapse>
			</Box>
		</>
	);
};

export default ProOnboardingChecklist;
